<template>
  <v-card>
    <form-header
      :form="form"
      :title="$lang('reservations.title_create')"
      tour="reservation-create-form"
    >
      <template v-slot:left-buttons>
        <v-btn icon dark @click="cancel()">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
      <template v-slot:toolbar-items>
        <v-btn
          v-if="step === 3 || form.type === $const('ConstReservationTypes', 'BLOCK')"
          text @click="submit()" :loading="loading" :disabled="loading"
          class="secondary"
          color="primary"
        >
          {{ $lang("main.crud.create") }}
        </v-btn>
      </template>
    </form-header>
    <v-card-text class="px-0">
      <v-container>
        <v-stepper v-model="step">
          <v-stepper-header v-if="form.type !== 3">
            <v-stepper-step :step="1">
              <span>{{ $lang('reservations.wizard.steps.search.title') }}</span>
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :step="2">
              <span>{{ $lang('reservations.wizard.steps.solutions.title') }}</span>
            </v-stepper-step>

            <v-divider></v-divider>

            <v-stepper-step :step="3">
              <span>{{ $lang('reservations.wizard.steps.creation.title') }}</span>
            </v-stepper-step>
          </v-stepper-header>

          <v-stepper-content :step="1" class="mx-0 px-0">
            <v-card flat>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="next()" v-if="form.type !== $const('ConstReservationTypes', 'BLOCK')" color="primary">{{ $lang('main.words.continue') }}</v-btn>
              </v-card-actions>
              <v-card-text>
                <v-row>
                  <v-col cols="12" xs="12" md="12" id="reservation-types-selection">
                    <v-btn-toggle v-model="form.type" class="hidden-xs-only" mandatory>
                      <v-btn id="reservation-btn" :disabled="$cannot('CREATE_RESERVATIONS_RESERVATION')" :value="$const('ConstReservationTypes', 'RESERVATION')" color="secondary">
                        {{ $const('ConstReservationTypes', 'RESERVATION', true)}}
                        <v-icon right>mdi-calendar</v-icon>
                      </v-btn>
                      <v-btn id="quote-btn" :disabled="$cannot('CREATE_RESERVATIONS_OPTIONS')" :value="$const('ConstReservationTypes', 'OPTION')" color="orange">
                        {{ $const('ConstReservationTypes', 'OPTION', true)}}
                        <v-icon right>mdi-calendar-question</v-icon>
                      </v-btn>
                      <v-btn id="blocks-btn" :disabled="$cannot('CREATE_RESERVATIONS_BLOCKS')" :value="$const('ConstReservationTypes', 'BLOCK')" color="amber">
                        {{ $const('ConstReservationTypes', 'BLOCK', true)}}
                        <v-icon right>mdi-account-hard-hat</v-icon>
                      </v-btn>
                    </v-btn-toggle>
                    <v-select
                      v-model="form.type"
                      :items="$in('ConstReservationTypes')"
                      :label="$lang('reservations.fields.type')"
                      class="d-flex d-sm-none"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-expand-transition>
                  <v-row>
                    <v-col cols="12" xs="12" md="8">
                      <v-card flat>
                        <v-card-title>
                          <span v-if="form.type === $const('ConstReservationTypes', 'RESERVATION')">
                            {{ $const('ConstReservationTypes', 'RESERVATION', true)}}
                          </span>
                          <span v-if="form.type === $const('ConstReservationTypes', 'OPTION')">
                            {{ $const('ConstReservationTypes', 'OPTION', true)}}
                          </span>
                          <span v-if="form.type === $const('ConstReservationTypes', 'BLOCK')">
                            {{ $const('ConstReservationTypes', 'BLOCK', true)}}
                          </span>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="mx-0 px-0" id="search-period">
                          <v-row align="center">
                            <v-col cols="12" xs="12" md="5">
                              <v-card>
                                <v-card-title>
                                  {{ $lang('reservations.fields.start_date') }}
                                </v-card-title>
                                <v-card-text>
                                  <v-date
                                    v-model="form.start_date"
                                    prepend-icon="mdi-airplane-landing"
                                    scrollable
                                    :no-title="true"
                                    :error="!!validation.start_date || !!validationMessages.start_date"
                                    :error-messages="!!validation.start_date ? validation.start_date : (!!validationMessages.start_date ? validationMessages.start_date : [])"
                                  ></v-date>
                                </v-card-text>
                              </v-card>
                            </v-col>
                            <v-col cols="2" class="hidden-xs-only">
                              <v-icon size="100">mdi-arrow-right</v-icon>
                            </v-col>
                            <v-col cols="12" xs="12" md="5">
                              <v-card>
                                <v-card-title>
                                  {{ $lang('reservations.fields.end_date') }}
                                </v-card-title>
                                <v-card-text>
                                  <v-date
                                    v-model="form.end_date"
                                    prepend-icon="mdi-airplane-takeoff"
                                    scrollable
                                    :no-title="true"
                                    :min="form.start_date"
                                    :error="!!validation.end_date || !!validationMessages.end_date"
                                    :error-messages="!!validation.end_date ? validation.end_date : (!!validationMessages.end_date ? validationMessages.end_date : [])"
                                  ></v-date>
                                </v-card-text>
                              </v-card>
                            </v-col>
                            <v-col cols="5" v-if="form.type === $const('ConstReservationTypes', 'OPTION')">
                              <v-datetime-picker
                                v-model="form.expired_at"
                                :text-field-props="{
                                  prependIcon: 'mdi-calendar',
                                  error: !!validationMessages.expired_at,
                                  errorMessages: !!validationMessages.expired_at ? validationMessages.expired_at : []
                                }"
                                :time-picker-props="{
                                  format: '24hr'
                                }"
                                :ok-text="$lang('main.words.ok')"
                                :clear-text="$lang('main.words.clear')"
                                :label="$lang('reservations.fields.expiration_date')"
                              >
                              <template v-slot:dateIcon>
                                <v-icon>mdi-calendar</v-icon>
                              </template>
                              <template v-slot:timeIcon>
                                <v-icon>mdi-clock</v-icon>
                              </template>
                              </v-datetime-picker>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>
                    <v-col cols="12" xs="12" md="4" v-if="form.start_date && form.end_date">
                      <v-card flat>
                        <v-card-title>
                          <span>{{ $lang('reservations.wizard.steps.search.attributes') }}</span>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text id="extra-search-params">
                          <v-row>
                            <v-text-field
                              v-model="form.children"
                              :label="$lang('reservations.fields.children')"
                            />
                            <template v-for="(child, index) in form.children_ages">
                              <v-text-field
                                v-model="form.children_ages[index]"
                                prepend-icon="mdi-baby-face-outline"
                                :label="$lang('reservations.fields.children_age')"
                                :error="!!validation[`children_ages.${index}`]"
                                :error-messages="!!validation[`children_ages.${index}`] ? validation[`children_ages.${index}`] : []"
                                dense
                                type="number"
                                :key="index"
                              />
                            </template>
                          </v-row>
                          <v-row>
                            <v-col cols="12" xs="12" md="12" v-if="form.type === $const('ConstReservationTypes', 'BLOCK')">
                              <v-autocomplete
                                v-model="form.property_id"
                                prepend-icon="mdi-office-building"
                                :items="properties"
                                :label="$lang('reservations.fields.property')"
                                :error="!!validation.property_id"
                                :error-messages="!!validation.property_id ? validation.property_id : []"
                              >
                              </v-autocomplete>
                              <c-units-autocomplete
                                v-model="form.unit_id"
                                :label="$lang('reservations.fields.unit')"
                                :filters="searchUnitsFilters"
                                :error="!!validation.unit_id"
                                :error-messages="!!validation.unit_id ? validation.unit_id : []"
                                :disabled="!form.property_id"
                              />
                              <v-text-field
                                v-model="form.block_reason"
                                prepend-icon="mdi-account-hard-hat"
                                :label="$lang('reservations.fields.block_reason')"
                                :error="!!validation.block_reason"
                                :error-messages="!!validation.block_reason ? validation.block_reason : []"
                              />
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-expand-transition>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="next()" v-if="form.type !== $const('ConstReservationTypes', 'BLOCK')" color="primary">{{ $lang('main.words.continue') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>

          <v-stepper-content :step="2">
            <v-card flat class="pa-0" v-if="step === 2">
              <v-card-actions>
                <v-btn @click="back()">{{ $lang('main.words.back') }}</v-btn>
              </v-card-actions>
              <v-card-text class="mx-0 px-0" id="combinations-block">
                <!-- <v-card outlined class="mb-2" v-if="!userData.is_single_property">
                  <v-card-title>
                    {{ $lang('main.words.filters') }}
                  </v-card-title>
                  <v-card-actions>
                     <v-autocomplete
                        v-model="form.property_id"
                        prepend-icon="mdi-office-building"
                        :items="properties"
                        :label="$lang('reservations.fields.property')"
                        :error="!!validation.property_id"
                        :error-messages="!!validation.property_id ? validation.property_id : []"
                        clearable
                      >
                      </v-autocomplete>
                      <v-spacer></v-spacer>
                      <v-btn color="success" @click="getCombinations(form)">
                        {{ $lang('main.words.search') }}
                      </v-btn>
                  </v-card-actions>
                </v-card> -->
                <!-- <v-skeleton-loader
                  v-show="combinationsRequest.loading"
                  :tile="false"
                  :boilerplate="false"
                  type="heading,table-row-divider@5"
                  class="mx-auto"
                ></v-skeleton-loader> -->
                <!-- <div v-if="!!combinations.length">
                  <div
                    v-show="!combinationsRequest.loading"
                    v-for="(combination, c) in combinations"
                    class="mt-2"
                    outlined
                    :key="c"
                  >
                    <combination-card
                      :combination="combination"
                      @selected="selectCombination"
                    ></combination-card>

                  </div>
                </div> -->
                <combinations-search
                  :options="form"
                  @selected="selectCombination"
                ></combinations-search>
                <!-- <v-col cols="12" align="center" v-if="!combinations.length && !combinationsRequest.loading">
                  <h2>{{ $lang('reservations.wizard.steps.solutions.no_records') }}</h2>
                  <v-img width="300px" src="/img/planning.svg"></v-img>
                  <v-btn @click="showAllCombinations" color="success">{{ $lang('reservations.wizard.show_unavailable') }}</v-btn>
                </v-col> -->

                <!-- <scroll-loader :loader-method="more" v-if="moreEnabled">
                </scroll-loader> -->
              </v-card-text>
              <v-card-actions>
                <v-btn @click="back()">{{ $lang('main.words.back') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>

          <v-stepper-content :step="3">
            <v-card flat>
              <v-card-actions>
                <v-btn @click="back()">{{ $lang('main.words.back') }}</v-btn>
                <v-spacer></v-spacer>
                <v-badge color="primary" overlap left>
                  <template v-slot:badge>
                    <v-icon>mdi-weather-night</v-icon>
                    {{ $dateDiffInDays(form.start_date, form.end_date) }}
                  </template>
                  <v-chip color="secondary" class="primary--text">
                    {{ $toDateFormat(form.start_date, 'DD/MM/YY') }}
                    <v-icon size="13" color="primary">mdi-arrow-right</v-icon>
                    {{ $toDateFormat(form.end_date, 'DD/MM/YY') }}
                  </v-chip>
                </v-badge>
                <v-color-picker-button
                  v-model="form.color"
                />
              </v-card-actions>
              <v-card-text class="px-0">
                <v-container class="px-0">
                  <v-row>
                    <v-col cols="12" xs="12" md="6" id="client-selection">
                      <v-client
                        :errors="validation.client_id"
                        :property-id="form.property_id"
                        @changed="(client) => {form.client_id = client.id}"
                        is-required
                      />
                    </v-col>
                    <v-col cols="12" xs="12" md="6">
                      <v-text-field
                        v-if="!!options.unit"
                        :value="options.unit.name"
                        prepend-icon="mdi-bed"
                        :label="$lang('reservations.fields.unit')"
                        :error="!!validation.unit_id"
                        :error-messages="!!validation.unit_id ? validation.unit_id : []"
                        disabled
                      >
                      </v-text-field>
                      <c-units-autocomplete
                        id="unit-selection"
                        v-else
                        v-model="form.unit_id"
                        :label="$lang('reservations.fields.unit')"
                        :filters="searchUnitsFilters"
                        :error="!!validation.unit_id"
                        :error-messages="!!validation.unit_id ? validation.unit_id : []"
                       />
                    </v-col>
                  </v-row>
                  <v-row v-if="form.summary">
                    <v-col cols="12" xs="12" md="8">
                      <v-row>
                        <v-col cols="12" xs="12" md="12" class="mt-0 pt-0 pb-1">
                          <h2>{{ form.summary.property }}</h2>
                          <h3>{{ $lang('reservations.fields.unit_category') }}: {{ form.summary.unit_category.name }}</h3>
                        </v-col>
                        <v-col cols="12" xs="6" md="4">
                          <v-img
                            v-if="form.summary.unit_category.main_image"
                            :src="form.summary.unit_category.main_image.full_path"
                          ></v-img>
                          <v-img
                            v-if="!form.summary.unit_category.main_image"
                            src="/img/room.svg"
                            width="100%"
                          ></v-img>
                        </v-col>
                        <v-col cols="12" xs="6" md="8">
                          <v-text-field
                            v-model="form.summary.rate_plan.name"
                            :label="$lang('reservations.fields.room_type')"
                            disabled
                          />
                          <v-text-field
                            v-model="form.summary.room_type.guests"
                            :label="$lang('reservations.fields.guests')"
                            disabled
                          />
                          <v-row>
                            <v-col>
                              <c-refundable-menu
                                v-model="form.is_refundable"
                                :label="$lang('main.words.refundability')"
                                x-small
                                block
                              />
                            </v-col>
                            <v-col>
                              <c-meal-plan-menu
                                v-model="form.meal_plan"
                                :label="$lang('main.words.meal_plan')"
                                x-small
                                block
                              />
                            </v-col>
                          </v-row>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" xs="12" md="4" id="totals-block">
                      <v-text-field
                        @change="totalChanged"
                        v-model="form.total"
                        type="number"
                        :suffix="form.summary.currency"
                        :label="$lang('reservations.fields.total')"
                        :hint="$lang('reservations.hints.total')"
                        persistent-hint
                        :error="!!validation.total"
                        :error-messages="!!validation.total ? validation.total : []"
                      >
                        <template v-slot:prepend>
                          <v-btn fab x-small color="primary" @click="openAmountDetails()">
                            <v-icon>mdi-cash</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                      <v-text-field
                        @change="discountChanged"
                        v-model="form.discount"
                        type="number"
                        :label="$lang('reservations.fields.discount')"
                        :error="!!validation.discount"
                        :error-messages="!!validation.discount ? validation.discount : []"
                      >
                        <template v-slot:prepend>
                          <v-btn fab x-small color="warning" @click="openPromotions()">
                            <v-icon>mdi-sale</v-icon>
                          </v-btn>
                        </template>
                        <template v-slot:append>
                          {{form.summary.currency}} / {{discountPercent}}%
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-model="form.extras"
                        :readonly="true"
                        type="number"
                        :suffix="form.summary.currency"
                        :label="$lang('reservations.fields.extras')"
                        :error="!!validation.amount_extras"
                        :error-messages="!!validation.amount_extras ? validation.amount_extras : []"
                      >
                        <template v-slot:prepend>
                          <v-btn fab x-small color="primary" @click="openAmountExtras()">
                            <v-icon>mdi-cash</v-icon>
                          </v-btn>
                        </template>
                      </v-text-field>
                      <v-text-field
                        v-model="form.cleaning_fee"
                        type="number"
                        prepend-icon="mdi-spray-bottle"
                        :suffix="form.summary.currency"
                        :label="$lang('reservations.fields.cleaning_fee')"
                        :error="!!validation.cleaning_fee"
                        :error-messages="!!validation.cleaning_fee ? validation.cleaning_fee : []"
                      />
                      <v-text-field
                        v-model="netTotal"
                        readonly
                        type="number"
                        prepend-icon="mdi-cash-multiple"
                        :suffix="form.summary.currency"
                        :label="$lang('reservations.fields.total_gross')"
                      />
                    </v-col>
                  </v-row>
                  <v-row id="extra-details-block">
                    <v-col cols="8" xs="12" md="8">
                      <v-row>
                        <v-col cols="12" xs="12" md="6">
                          <v-textarea
                            v-model="form.internal_note"
                            :label="$lang('reservations.fields.internal_note')"
                            filled
                            rows="2"
                            :error="!!validation.internal_note"
                            :error-messages="!!validation.internal_note ? validation.internal_note : []"
                          />
                        </v-col>
                        <v-col cols="12" xs="12" md="6">
                          <v-textarea
                            v-model="form.service_note"
                            :label="$lang('reservations.fields.service_note')"
                            filled
                            rows="2"
                            :error="!!validation.service_note"
                            :error-messages="!!validation.service_note ? validation.service_note : []"
                          />
                        </v-col>
                      </v-row>
                      <v-row>
                        <v-col cols="12" xs="12" md="12">
                          <v-textarea
                            v-model="form.ota_note"
                            :label="$lang('reservations.fields.ota_note')"
                            :hint="$lang('reservations.hints.ota_note')"
                            background-color="amber lighten-4"
                            filled
                            rows="2"
                            :error="!!validation.ota_note"
                            :error-messages="!!validation.ota_note ? validation.ota_note : []"
                          />
                        </v-col>
                      </v-row>
                      <div class="subtitle-1">
                        {{ $lang('reservations.labels.occupant') }}
                      </div>
                      <v-row>
                        <v-col>
                          <v-text-field
                            v-model="form.given_name"
                            :label="$lang('reservations.fields.given_name')"
                          />
                        </v-col>
                        <v-col>
                          <v-text-field
                            v-model="form.family_name"
                            :label="$lang('reservations.fields.family_name')"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="12" xs="12" md="4">
                      <v-text-field
                        v-model="form.arrival_time"
                        prepend-icon="mdi-clock"
                        :label="$lang('reservations.fields.arrival_time')"
                        type="time"
                        :error="!!validation.arrival_time"
                        :error-messages="!!validation.arrival_time ? validation.arrival_time : []"
                      />
                      <v-text-field
                        v-model="form.guests"
                        type="number"
                        prepend-icon="mdi-human-male"
                        :label="$lang('reservations.fields.guests')"
                        :error="!!validation.guests"
                        :error-messages="!!validation.guests ? validation.guests : []"
                        @input="checkMaxOccupation()"
                      />
                      <v-text-field
                        v-model="form.children"
                        type="number"
                        prepend-icon="mdi-baby-face-outline"
                        :label="$lang('reservations.fields.children')"
                        :hint="$lang('reservations.hints.children')"
                        persistent-hint
                        :error="!!validation.children"
                        :error-messages="!!validation.children ? validation.children : []"
                      />
                      <v-text-field
                        v-model="form.infants"
                        type="number"
                        prepend-icon="mdi-baby-carriage"
                        :label="$lang('reservations.fields.infants')"
                        :hint="$lang('reservations.hints.infants')"
                        persistent-hint
                        :error="!!validation.infants"
                        :error-messages="!!validation.infants ? validation.infants : []"
                      />
                      <v-select
                        :items="$in('ConstReservationPaymentMethods')"
                        v-model="form.payment_method"
                        :label="$lang('reservations.fields.payment_method')"
                        prepend-icon="mdi-piggy-bank"
                        :error="!!validation.payment_method"
                        :error-messages="!!validation.payment_method ? validation.payment_method : []"
                      />
                      <v-boolean
                        v-model="form.skip_messages"
                        :label="$lang('reservations.labels.skip_messages')"
                        :hint="$lang('reservations.hints.skip_messages')"
                        persistent-hint
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-btn @click="back()">{{ $lang('main.words.back') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-stepper-content>
        </v-stepper>
      </v-container>
    </v-card-text>

    <v-dialog v-model="isOpenAmountDetails" transition="dialog-bottom-transition" width="30%">
      <amount-details-form
        v-if="isOpenAmountDetails"
        :form-options="form"
        :amount-details="amountDetails"
        @close="closeAmountDetails"
        @changed="changeAmountDetails"
        @apply="changeAmountDetails"
      />
    </v-dialog>

    <v-dialog v-model="isOpenPromotions" transition="dialog-bottom-transition" width="30%">
      <promotions-form
        v-if="isOpenPromotions"
        :options="{
          end_date: form.end_date,
          start_date: form.start_date,
          property_id: form.property_id,
        }"
        @close="closePromotions"
        @applied="promotionApplied"
      />
    </v-dialog>

    <v-dialog v-model="isOpenAmountExtras" transition="dialog-bottom-transition" width="50%">
      <amount-extras-form
        v-if="isOpenAmountExtras"
        :extras="form.amount_extras || []"
        :form-options="form"
        @close="closeAmountExtras"
        @changed="changeAmountExtras"
      />
    </v-dialog>
  </v-card>
</template>

<script>
import { mapState } from "vuex";
import MixpanelEvents from "@/constants/events";

import {
  GET_BOOKING_ENGINE_COMBINATIONS,
} from "@/store/booking-engine/actions";

import {
  CREATE_RESERVATIONS_WIZARD,
  CREATE_RESERVATIONS_WIZARD_RESET
} from "@/store/reservations/actions";

import {
  GET_PROPERTY_EXTRAS_COLLECTION,
} from "@/store/property-extras/actions";

import form from "@/pages/reservations/mixins/form";
import validator from "@/pages/reservations/mixins/validate-wizard";
import extraComputation from "@/pages/reservation-extras/mixins/extras-computation";

import CombinationCard from "@/pages/reservations/partials/CombinationCard";
import CombinationsSearch from "@/pages/reservations/partials/CombinationsSearch";
import AmountExtrasForm from "@/pages/reservations/partials/AmountExtrasForm";
import AmountDetailsForm from "@/pages/reservations/partials/AmountDetailsForm";

export default {
  name: "CreationWizardForm",
  mixins: [
    form,
    validator,
    extraComputation,
  ],
  components: {
    CombinationCard,
    CombinationsSearch,
    AmountExtrasForm,
    AmountDetailsForm
  },
  data() {
    return {
      step: 1,
      isOpenAmountExtras: false,
      isOpenAmountDetails: false,
      grossAmount: null,
      discountPercent: null,
      amountDetails: [],
    };
  },
  beforeMount() {
    if (!this.$isEmpty(this.options)) {
      this.mountOptions();
    }
  },
  methods: {
    initForm(source) {
      this.form = {
        ...this.form,
        ...source
      };

      this.amountDetails = source.amount_details;
    },
    resetAction() {
      return CREATE_RESERVATIONS_WIZARD_RESET;
    },
    submitAction() {
      return CREATE_RESERVATIONS_WIZARD;
    },
    submitActionOptions() {
      return this.prepare();
    },
    track() {
      this.$monitorService.track(MixpanelEvents.SINGLE_RESERVATION_CREATED, {
        type: this.$constName('ConstReservationTypes', this.form.type),
      });
    },
    next() {
      if (!this.validate()) {
        this.step++;
      }

      // if (this.step === 2) {
      //   if (!this.form.property_id) {
      //     this.getProperties();
      //   }
      //   this.getCombinations(this.form);
      // }
      if (this.step === 3) {
        this.getPropertyExtras();
      }
    },
    back() {
      this.step--;
      this.form.amount_details = [];
      delete this.form['summary'];
    },
    getCombinations(data) {
      if (this.options.unit) {
        data.unit_category_id = this.options.unit.unit_category_id;
      }

      this.$store.dispatch(GET_BOOKING_ENGINE_COMBINATIONS, data);
    },
    selectCombination(combination) {
      this.initForm(combination);
      this.form.total = this.calculateTotal();
      this.form.discount = null;
      this.form.guests = combination.guests;
      this.next();
    },
    showAllCombinations() {
      this.form.all = 1;
      this.getCombinations(this.form);
    },
    calculateTotal() {
      let total = 0;

      this.amountDetails.map((detail) => {
        total += parseFloat(detail.amount)
      });

      return total.toFixed(2);
    },
    calculateDiscount() {
      let discount = 0;

      this.amountDetails.map((detail) => {
        discount += parseFloat(detail.discount);
      });

      return discount.toFixed(2);
    },
    totalChanged() {
      if (!this.form.total || isNaN(this.form.total)) {
        return;
      }

      let endDate = this.$moment(this.form.end_date);
      let startDate = this.$moment(this.form.start_date);
      let totalAmount = parseFloat(this.form.total);

      const nights = this.$dateDiffInDays(startDate, endDate);
      const amount = !isNaN(totalAmount/nights) ? (totalAmount/nights).toFixed(3) : 0;

      this.amountDetails = [];

      do {
        this.amountDetails.push({
          room_type_id: this.form.room_type_id,
          amount: amount,
          date: this.$toDateString(startDate),
        });

        startDate.add(1, 'days');
      } while (endDate.isAfter(startDate));
      this.discountChanged();
      this.setDiscountPercent();
    },
    discountChanged() {
      if (!this.form.discount || isNaN(this.form.discount)) {
        return;
      }

      let endDate = this.$moment(this.form.end_date);
      let startDate = this.$moment(this.form.start_date);

      const nights = this.$dateDiffInDays(startDate, endDate);
      const amount = !isNaN(this.form.total/nights) ? (this.form.total/nights).toFixed(3) : 0;
      const discount = !isNaN(this.form.discount/nights) ? (this.form.discount/nights).toFixed(3) : 0;

      this.amountDetails = [];

      do {
        this.amountDetails.push({
          room_type_id: this.form.room_type_id,
          amount: amount,
          discount: discount,
          date: this.$toDateString(startDate),
        });

        startDate.add(1, 'days');
      } while (endDate.isAfter(startDate));
      this.setDiscountPercent();
    },
    prepare() {
      let data = { ...this.form };

      if (data.type === this.$const('ConstReservationTypes', 'OPTION')) {
        data.is_refundable = data.summary.room_type.refundable;
        data.expired_at = this.$toDateTimeString(data.expired_at);
      }
      if (data.type === this.$const('ConstReservationTypes', 'RESERVATION')) {
        data.is_refundable = data.summary.room_type.refundable;
        delete data['expired_at'];
      }
      if (data.type === this.$const('ConstReservationTypes', 'BLOCK')) {
        delete data['guests'];
        delete data['children'];
        delete data['expired_at'];
      }

      data.amount_details = this.amountDetails.map(item => {
        let detail = {...item};

        if (!!data.discount && !isNaN(data.discount)) {
          detail.amount = parseFloat(item.amount) - parseFloat(item.discount);
        }

        return detail;
      });

      delete data['summary'];
      delete data['all'];
      delete data['total'];
      delete data['discount'];

      return data;
    },
    more() {
      this.getCombinations({
        ...this.form,
        page: this.pagination.page + 1
      });

      this.combinationsRequest.loading = false;
    },
    mountOptions() {
      if (this.options.step) {
        this.step = this.options.step;
      }
      if (this.options.end_date) {
        this.form.end_date = this.options.end_date;
      }
      if (this.options.start_date) {
        this.form.start_date = this.options.start_date;
      }
      if (this.options.unit) {
        this.form.unit_id = this.options.unit.id;
        this.form.unit_category_id = this.options.unit.unit_category_id;

        if (this.step === 2) {
          this.getCombinations({
            ...this.form
          });
        }
      }
      if (this.options.property) {
        this.form.property_id = this.options.property.id;
        this.form.currency = this.options.property.currency;
      }
    },
    openAmountDetails() {
      if (!this.amountDetails || !this.amountDetails.length) {
        return;
      }

      this.isOpenAmountDetails = true;
    },
    closeAmountDetails() {
      this.isOpenAmountDetails = false;
    },
    changeAmountDetails(payload) {
      this.isOpenAmountDetails = false;
      this.form.discount = 0;
      this.form.total = this.calculateTotal();
      this.initForm(payload);
    },
    promotionApplied(promotion) {
      this.appliedPromotion = promotion;

      this.amountDetails.map(detail => {
        const oldAmount = detail.amount;
        const discountedAmount = this.calculateSinglePrice(detail, {
          promotion: this.appliedPromotion
        });
        detail.discount = oldAmount - discountedAmount;
      });

      this.form.discount = this.calculateDiscount();
      this.form.relations = [
        {
          name: 'reservation_promotions',
          data: [this.appliedPromotion.id]
        }
      ];
      this.setDiscountPercent();
    },
    setDiscountPercent() {
      if (!this.form.discount) return 0;

      this.discountPercent = Math.round((this.form.discount/this.form.total) * 100);
    },
    getPropertyExtras() {
      this.$store.dispatch(GET_PROPERTY_EXTRAS_COLLECTION, {
        active: true,
        property_id: this.form.property_id,
        valid: this.form.start_date,
      }).then(({ collection }) => {
        this.form.amount_extras = [];

        collection.forEach(item => {
          if (!item.is_required) {
            return;
          }

          this.form.amount_extras.push({
            name: item.name,
            amount: this.computeAmount(item, this.form),
            property_extra_id: item.id,
            vat_percent: item.vat_percent,
            is_required: item.is_required,
          });
        });

        this.form.extras = this.calculateExtras();
        this.$forceUpdate();
      });
    },
    calculateExtras() {
      if (!this.form.amount_extras) {
        return 0;
      }

      let extras = 0;

      this.form.amount_extras.map(extra => {
        extras += parseFloat(extra.amount);
      });

      return extras.toFixed(2);
    },
    changeAmountExtras(payload) {
      this.form.amount_extras = [ ...payload ];
      this.form.extras = this.calculateExtras();
    },
    openAmountExtras() {
      this.isOpenAmountExtras = true;
    },
    closeAmountExtras() {
      this.isOpenAmountExtras = false;
    },
    checkMaxOccupation() {
      if (
          !this.form.summary.unit_category ||
          this.form.guests < this.form.summary.unit_category.max_occupation
        ) {
          return;
        }

        this.showNotification(this.$lang('reservations.labels.max_occupation_exceed'), 'warning');
    }
  },
  computed: {
    pagination() {
      return this.combinationsRequest.data.pagination || {};
    },
    combinations() {
      return this.combinationsRequest.data.collection || [];
    },
    moreEnabled() {
      return this.pagination.page < this.pagination.totalPages;
    },
    netTotal() {
      let amount = parseFloat(this.calculateTotal());
      let cleaningFee = parseFloat(this.form.cleaning_fee);
      let discount = !!this.form.discount ? parseFloat(this.form.discount) : 0;
      let extras = !!this.form.extras ? parseFloat(this.form.extras) : 0;

      return (amount + cleaningFee - discount + extras).toFixed(2);
    },
    ...mapState({
      loading: state => state.reservations.creationWizardRequest.loading,
      validation: state => state.reservations.creationWizardRequest.validation,
      combinationsRequest: state => state.bookingEngine.getCombinationsRequest,
    }),
  },
  watch: {
    'form.room_type_id'() {
      this.searchUnits();
    },
    'form.type'(val) {
      if (val === this.$const('ConstReservationTypes', 'BLOCK')) {
        this.getProperties();

        if (this.options.property) {
          this.searchUnits();
        }
      }
    },
    'form.children'(val) {
      this.form.children_ages = [];

      for (let i = 0; i < val; i++) {
        this.form.children_ages.push(null);
      }
    }
  }
};
</script>
